import { ChangeEvent, useEffect, useState } from "react";
import {
	faArrowUp,
	faFileExport,
	faPlus,
	faPowerOff,
	faShieldAlt,
	faSync,
	faTrash
} from "@fortawesome/free-solid-svg-icons";

import useData from "../app/useData";
import { COLLISION_MODE_LIST } from "../app/Data";
import { downloadFile } from "../utils/fileUtils";

import ElementList from "./ElementList";
import ButtonIcon from "./ButtonIcon";
import Form from "./Form";
import ToolboxImport from "./ToolboxImport";

import "./Toolbox.css";

const COLLISION_MODE_LABEL_MAP = {
	IGNORE: "Ignore",
	MOVE_UP: "Move Up",
	MOVE_PAST: "Move Past",
	BLOCK: "Block"
};
const COLLISION_MODE_ICON_MAP = {
	IGNORE: faPowerOff,
	MOVE_UP: faArrowUp,
	MOVE_PAST: faSync,
	BLOCK: faShieldAlt
};
const DEFAULT_CONTAINER = { id: "", width: 0, height: 0, depth: 0, content: [] };
const Toolbox: React.FC<{}> = () => {
	const data = useData();
	const {
		containers,
		containerEditingId: containerId,
		selectContainer,
		deleteContainer,
		newContainer,
		importData,
		exportData,
		collisionMode,
		setCollisionMode
	} = data;
	const [editing, setEditing] = useState<ContainerType>(DEFAULT_CONTAINER);
	useEffect(() => {
		const container = containerId && containers.get(containerId);
		setEditing(container || DEFAULT_CONTAINER);
	}, [containers, containerId]);

	const updateEditing: (
		event: ChangeEvent<HTMLInputElement>,
		args: { name: string; value: string | number }
	) => void = (_, { name, value }) =>
		setEditing(editing => editing && { ...editing, [name]: value });

	return (
		<div className="Toolbox">
			<div className="title">
				Toolbox Tetris Your Furnitures 3D
				<ToolboxImport onImport={importData} />
				<ButtonIcon
					icon={faFileExport}
					onClick={() =>
						downloadFile(
							`data:application/json;base64,${Buffer.from(exportData()).toString(
								"base64"
							)}`,
							"tyf3d.json"
						)
					}
				/>
			</div>
			<div className="collisionMode">
				<span>Collisions</span>
				{COLLISION_MODE_LIST.map(mode => (
					<ButtonIcon
						icon={COLLISION_MODE_ICON_MAP[mode]}
						title={COLLISION_MODE_LABEL_MAP[mode]}
						inverted={collisionMode === mode}
						onClick={() => setCollisionMode(mode)}
					/>
				))}
			</div>
			<div className="containers">
				<select
					onChange={e => selectContainer(e.target.value)}
					value={containerId || ""}
					className={containerId ? undefined : "none"}
				>
					<option value="">New...</option>
					{Array.from(containers.values()).map(({ id }) => (
						<option key={id} value={id}>
							{id}
						</option>
					))}
				</select>
				{containerId ? (
					<ButtonIcon
						icon={faTrash}
						className="containerRemove"
						onClick={() => deleteContainer(containerId)}
					/>
				) : (
					<ButtonIcon
						icon={faPlus}
						className="containerRemove"
						onClick={() => newContainer(editing)}
					/>
				)}
			</div>
			{!containerId && (
				<Form>
					<Form.Labels>
						<Form.Field>Id</Form.Field>
						<Form.Field>Width</Form.Field>
						<Form.Field>Height</Form.Field>
						<Form.Field>Depth</Form.Field>
					</Form.Labels>
					<Form.Values>
						<Form.Input
							name="id"
							type="text"
							value={editing.id}
							onChange={updateEditing}
						/>
						<Form.InputNumber
							name="width"
							value={editing.width}
							onChange={updateEditing}
						/>
						<Form.InputNumber
							name="height"
							value={editing.height}
							onChange={updateEditing}
						/>
						<Form.InputNumber
							name="depth"
							value={editing.depth}
							onChange={updateEditing}
						/>
					</Form.Values>
				</Form>
			)}
			<div className="content">
				<ElementList />
			</div>
		</div>
	);
};

export default Toolbox;
